import { useWindowSize } from "hooks/useWindowSize";
import Loader from "components/DesignSystem/Loader/loader";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Filter } from "components/DesignSystem/Filter/Filter";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { Search } from "components/DesignSystem/Search/Search";
import { PriceInput } from "components/PriceInput/PriceInput";
import {
  Capsule,
  isChecked,
  DateRangeFilter,
} from "components/Transaction/Filter/Filter";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { parse } from "qs";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getLedgerFilterStatus } from "store/selector/legderFilter";
import {
  FilterName,
  resetLedgerFilter,
  setLedgerFilter,
} from "store/slices/generalLedger";
import { TxnCategories } from "types/Models/books";
import { debounce } from "utils/debouncing";
import { flattenTypes } from "utils/flattenCOA";
import { CategoryLabel } from "components/design/CategoryLabel";
import { useFilters } from "hooks/useFilter";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { CASH_FLOW, CASH_FLOW_LABEL } from "constants/transaction";
import {
  ACCOUNTING_METHODS,
  ACCOUNTING_METHODS_OPTIONS,
  GENERAL_LEDGER,
  REPORTS_LABELS,
  TRIAL_BALANCE,
} from "constants/bookkeeping";
import { PERIOD } from "constants/revenueMetrics";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { Button } from "components/DesignSystem/Button/Button";
import { ArrowRight } from "components/icons/ArrowRight";

export const TransactionsForWrapper = ({
  children,
  queryUpdate = false,
  updateDates,
}: {
  queryUpdate?: boolean;
  children: ({
    handleChange,
    handleDateChange,
  }: {
    handleChange: ({ value }: any) => void;
    handleDateChange: ({ date, name }: { date: Date; name: string }) => void;
  }) => ReactNode;
  updateDates?: ({
    startDate,
    endDate,
  }: {
    startDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;
  }) => void;
}) => {
  const { update, updateMultiple } = useUpdateQuery();

  const { setFieldValue } = useFormikContext();

  const handleDateChange = debounce(
    ({ date, name }: { date: Date; name: string }) => {
      if (date && isNaN(date.valueOf())) {
        return;
      }

      if (queryUpdate) {
        update({
          query: name,
          value: date ? dayjs(date).format(YYYY_MM_DD) : null,
        });
      }
    }
  );

  const handleMultipleDateChange = ({
    startDate,
    endDate,
  }: {
    startDate: Date;
    endDate: Date;
  }) => {
    if (queryUpdate) {
      updateMultiple([
        {
          query: "startDate",
          value: startDate ? dayjs(startDate).format(YYYY_MM_DD) : null,
        },
        {
          query: "endDate",
          value: endDate ? dayjs(endDate).format(YYYY_MM_DD) : null,
        },
      ]);
    }
  };

  const updateFieldValues = ({
    startDate,
    endDate,
  }: {
    startDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;
  }) => {
    setFieldValue("startDate", startDate.format(DISPLAY_DATE));
    setFieldValue("endDate", endDate.format(DISPLAY_DATE));
    handleMultipleDateChange({
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
    });
    updateDates?.({ startDate, endDate });
  };

  const handleChange = ({ value }: { value: string }) => {
    setFieldValue("transactionsFor", value);
    switch (value) {
      case "lastmonth": {
        updateFieldValues({
          startDate: dayjs().subtract(1, "month").startOf("month"),
          endDate: dayjs().subtract(1, "month").endOf("month"),
        });
        return;
      }
      case "last3months": {
        updateFieldValues({
          startDate: dayjs().subtract(3, "month").startOf("month"),
          endDate: dayjs().subtract(1, "month").endOf("month"),
        });
        return;
      }

      case "currentYear": {
        updateFieldValues({
          startDate: dayjs().startOf("year"),
          endDate: dayjs(),
        });

        return;
      }

      case "lastYear": {
        updateFieldValues({
          startDate: dayjs().subtract(1, "year").startOf("year"),
          endDate: dayjs().subtract(1, "year").endOf("year"),
        });

        return;
      }

      case "last30days": {
        updateFieldValues({
          startDate: dayjs().subtract(30, "days"),
          endDate: dayjs(),
        });
        return;
      }
      case "last90days": {
        updateFieldValues({
          startDate: dayjs().subtract(90, "days"),
          endDate: dayjs(),
        });
        return;
      }
    }
  };

  return <>{children({ handleChange, handleDateChange })}</>;
};

const Amount = () => {
  const { update } = useUpdateQuery();
  const { filters } = useSelector(getLedgerFilterStatus);
  const [cashFlow, minAmount, maxAmount] = filters.amount;

  const handleRadioCheck = (
    value: string,
    setValues: FormikHelpers<{
      minAmount: string;
      maxAmount: string;
    }>["setValues"]
  ) => {
    if (value === "") {
      setValues({ minAmount: "", maxAmount: "" });
      update({ query: minAmount, value: null });
      update({ query: maxAmount, value: null });
    }
    update({
      query: "cashFlow",
      value,
    });
  };

  const handleAmountChange = debounce((e: ChangeEvent<HTMLFormElement>) => {
    const { value, name } = e.target;
    update({ query: name, value });
  });

  return (
    <Formik
      initialValues={{
        minAmount: minAmount.value || "",
        maxAmount: maxAmount.value || "",
      }}
      onSubmit={() => {}}
    >
      {({ setValues }) => {
        return (
          <div className="t-flex t-flex-col t-gap-5">
            <div className="t-flex t-flex-col t-gap-3">
              <div className="t-text-body-sm t-text-neutral-80 t-w-full">
                Type
              </div>
              <Radio.Root
                onValueChange={(value) => handleRadioCheck(value, setValues)}
                /* @ts-ignore */
                defaultValue={cashFlow?.value || ""}
              >
                <Radio.Content>
                  <Radio.Item asChild value={""}>
                    Any
                  </Radio.Item>
                  <Radio.Item asChild value={CASH_FLOW.CREDIT}>
                    {CASH_FLOW_LABEL[CASH_FLOW.CREDIT]}
                  </Radio.Item>
                  <Radio.Item asChild value={CASH_FLOW.DEBIT}>
                    {CASH_FLOW_LABEL[CASH_FLOW.DEBIT]}
                  </Radio.Item>
                </Radio.Content>
              </Radio.Root>
            </div>
            <div className="t-flex t-flex-col t-gap-3">
              <Form
                className="all:unset t-flex t-flex-col t-gap-4"
                onChange={handleAmountChange}
              >
                <PriceInput
                  name="minAmount"
                  label="Min. Amount"
                  placeholder="0.00"
                  disabled={Boolean(!cashFlow?.value)}
                />
                <PriceInput
                  name="maxAmount"
                  placeholder="0.00"
                  label="Max. Amount"
                  disabled={Boolean(!cashFlow?.value)}
                />
              </Form>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

const AccountingMethod = () => {
  const dispatch = useDispatch();
  const { filters } = useSelector(getLedgerFilterStatus);
  const { update } = useUpdateQuery();
  const [accountingMethod] = filters.accountingMethod;

  const onChange = (value: string) => {
    dispatch(setLedgerFilter({ accountingMethod: value }));
    update({
      query: "accountingMethod",
      value: value,
    });
  };

  return (
    <>
      <Label>Accounting method</Label>
      <Radio.Root
        onValueChange={onChange}
        value={accountingMethod?.value || ""}
      >
        <Radio.Content className="t-gap-3 t-flex t-flex-col">
          {ACCOUNTING_METHODS_OPTIONS.map((item) => (
            <Radio.Item key={item.value} value={item.value}>
              {item.label}
            </Radio.Item>
          ))}
        </Radio.Content>
      </Radio.Root>
    </>
  );
};

export const DropDownItem = ({
  types,
  level,
  onHandleChange,
  categoryIds = [],
}: {
  types?: TxnCategories[];
  level: number;
  onHandleChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    types?: TxnCategories[]
  ) => void;
  categoryIds?: string[];
}) => {
  const maxIndent = 2;
  let nextLevel = level;
  if (nextLevel <= maxIndent) {
    nextLevel = nextLevel + 1;
  }

  return (
    <div>
      {types?.map((type) => (
        <div key={type.uuid}>
          <Dropdown.Item
            className="t-flex t-gap-1.5 hover:!t-bg-surface-transparent"
            onSelect={(e) => {
              e.preventDefault();
            }}
            textValue=""
          >
            <Checkbox
              name={type.uuid}
              value={type.uuid}
              label={<CategoryLabel category={type} />}
              onChange={(e) => onHandleChange(e, type?.types)}
              checked={Boolean(isChecked(categoryIds, type.uuid))}
            />
          </Dropdown.Item>
          <div style={{ paddingLeft: nextLevel > maxIndent ? "0px" : "16px" }}>
            <DropDownItem
              types={type.types}
              level={nextLevel}
              onHandleChange={onHandleChange}
              categoryIds={categoryIds}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const CategoryFilter = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const { chartOfAccounts, isLoading } = useChartOfAccounts({
    search,
    hiddenCategoryTypes: ["BANK_TRANSFER", "PAY_DOWN_CREDIT"],
  });
  const { filters } = useSelector(getLedgerFilterStatus);
  const categoryIds = filters.category?.[0]?.value || [];

  const onHandleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    types?: TxnCategories[]
  ) => {
    const { checked, value } = e.target;
    const selectedChilds =
      flattenTypes({ accounts: types! })?.map(({ uuid }) => uuid) || [];
    if (Array.isArray(categoryIds)) {
      let newCategories: Set<string> = new Set();
      if (checked) {
        newCategories = new Set([...categoryIds, ...selectedChilds, value]);
      } else {
        newCategories = new Set(
          [...categoryIds]?.filter(
            (categoryId) =>
              !selectedChilds.includes(categoryId) && categoryId !== value
          )
        );
      }
      dispatch(setLedgerFilter({ categoryIds: [...newCategories] }));
    }
  };

  const handleSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value.toLowerCase() || "");
  });

  const noCategoriesFound = chartOfAccounts.length === 0;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="t-w-full t-flex t-flex-col t-gap-5 t-h-full">
      <Dropdown.Root onOpenChange={() => setSearch("")}>
        <Dropdown.Trigger asChild>
          <div>
            <label className="t-text-body-sm t-text-text-30 t-mb-1.5">
              Location of the new account
            </label>
            <div className="all:unset form-input w-full t-box-border t-flex t-h-10 t-w-[100%] t-items-center t-rounded t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-px-4 t-pr-5 t-font-sans t-text-body-sm !t-font-medium t-text-text-100 t-transition-all">
              <div className="t-overflow-hidden t-text-ellipsis t-whitespace-nowrap t-text-text-30">
                {categoryIds?.length > 0
                  ? categoryIds.length + " categories selected"
                  : "Select a parent account"}
              </div>
            </div>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            sideOffset={6}
            align="start"
            className="t-relative t-w-[430px]"
          >
            <Search
              placeholder="Search..."
              onChange={handleSearch}
              autoFocus
              customSize="regular"
              block
            />
            {noCategoriesFound ? (
              <div className="t-my-1.5 t-flex t-h-48 t-items-center t-justify-center">
                No categories found
              </div>
            ) : (
              <div className="t-max-h-72 t-overflow-auto">
                {chartOfAccounts?.map((account) => (
                  <div key={account.uuid}>
                    <Dropdown.Label className="t-bg-neutral-0 !t-text-text-60">
                      {account.identifier + "-" + account.name}
                    </Dropdown.Label>
                    <DropDownItem
                      types={account.types}
                      level={0}
                      onHandleChange={onHandleChange}
                      categoryIds={categoryIds as string[]}
                    />
                  </div>
                ))}
              </div>
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </div>
  );
};

const OtherFilters = () => {
  const { update } = useUpdateQuery();
  const {
    filters: { others },
    getFilterName,
  } = useSelector(getLedgerFilterStatus);
  const dispatch = useDispatch();

  const handleCheckBox = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;

    if (checked) {
      update({
        query: name,
        value: checked,
      });
      //@ts-ignore
      dispatch(setLedgerFilter({ [name]: checked }));
      return;
    }
    update({
      query: name,
      value: null,
    });
    dispatch(setLedgerFilter({ [name]: undefined }));
  };
  return (
    <div className="t-flex t-flex-col t-gap-3">
      {others.map(({ name, value }) => (
        <Checkbox
          key={name}
          name={getFilterName(name)}
          checked={Boolean(value)}
          label={name}
          onChange={handleCheckBox}
        />
      ))}
    </div>
  );
};

const CapsuleFilters = ({ entityId }: { entityId: string }) => {
  const { update, updateMultiple } = useUpdateQuery();
  const dispatch = useDispatch();
  const query = useQuery();
  const startDate = query.get("startDate");
  const endDate = query.get("endDate");
  const accountingMethod = query.get("accountingMethod");
  const { capsuleFilters, getFilterName } = useSelector(getLedgerFilterStatus);

  const { data: settings } = useGetBookkeepingSettingsQuery(
    {
      entityId,
    },
    { skip: !entityId }
  );

  const handleClick = ({ name }: { name: FilterName }) => {
    update({ query: name, value: null });
    dispatch(setLedgerFilter({ [name]: undefined }));
  };

  useEffect(() => {
    if (!endDate && !startDate && !accountingMethod && settings) {
      updateMultiple([
        {
          query: "startDate",
          value: dayjs()
            .subtract(1, "month")
            .startOf("month")
            .format(YYYY_MM_DD),
        },
        {
          query: "endDate",
          value: dayjs().subtract(1, "month").endOf("month").format(YYYY_MM_DD),
        },
        { query: "accountingMethod", value: settings?.accounting_method || "" },
      ]);
    }
  }, [startDate, endDate, accountingMethod, settings?.accounting_method]);

  return (
    <>
      {capsuleFilters.map(({ name, value, type }) => {
        return (
          <Capsule
            key={name}
            value={
              type === "transactionDate" && typeof value === "string"
                ? dayjs(value).format(DISPLAY_DATE)
                : Array.isArray(value) && (value as unknown as []).length > 0
                ? `(${(value as unknown as []).length})`
                : value
            }
            onCapsuleClick={() => {
              if (
                !(
                  (type === "transactionDate" || type === "accounting") &&
                  typeof value === "string"
                )
              ) {
                handleClick({ name: getFilterName(name)! as FilterName });
              }
            }}
            isRemovable={
              type === "transactionDate" || type === "accounting" ? false : true
            }
            type={type}
          >
            {name}
          </Capsule>
        );
      })}
    </>
  );
};

type ReportType = typeof GENERAL_LEDGER | typeof TRIAL_BALANCE;

type ViewAsFilterProps = {
  reportType: ReportType;
  groupBy: string | null;
  onReportTypeChange: (reportType: ReportType, groupBy: string | null) => void;
};

const ViewAsFilter = ({
  reportType,
  groupBy,
  onReportTypeChange,
}: ViewAsFilterProps) => {
  const isTrialBalance = reportType === TRIAL_BALANCE;
  const isMonthGroupBy = groupBy === PERIOD.MONTHLY;

  const title = isTrialBalance
    ? `${REPORTS_LABELS.TRIAL_BALANCE}${isMonthGroupBy ? " (MoM)" : ""}`
    : REPORTS_LABELS.GENERAL_LEDGER;

  const handleGeneralLedgerSelect = () => {
    onReportTypeChange(GENERAL_LEDGER, null);
  };

  const handleTrialBalanceSelect = (selectedGroupBy: string | null) => {
    onReportTypeChange(TRIAL_BALANCE, selectedGroupBy);
  };

  return (
    <Dropdown.Root modal={false}>
      <Dropdown.Trigger className="all:unset t-group t-self-center">
        <Button size="xsmall">
          <span className="t-text-text-30">View as:</span>
          <span className="t-ml-1 t-text-text-60">{title}</span>
          <div className="t-text-text-30 t-transition-transform t-duration-200 t-rotate-90 group-data-[state=open]:-t-rotate-90">
            <ArrowRight color="currentColor" size="22" stroke="1.25" />
          </div>
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          sideOffset={8}
          className="!t-min-w-[12rem] t-overflow-y-auto"
          align="start"
        >
          <Dropdown.Item
            onSelect={handleGeneralLedgerSelect}
            className={!isTrialBalance ? "t-bg-purple-0" : ""}
          >
            {REPORTS_LABELS.GENERAL_LEDGER}
          </Dropdown.Item>
          <Dropdown.Sub>
            <Dropdown.SubTrigger
              className={isTrialBalance ? "t-bg-purple-0" : ""}
            >
              {REPORTS_LABELS.TRIAL_BALANCE}
            </Dropdown.SubTrigger>
            <Dropdown.Portal>
              <Dropdown.SubContent sideOffset={8} className="!t-min-w-48">
                <Dropdown.Label>Group by</Dropdown.Label>
                <Dropdown.Item
                  onSelect={() => handleTrialBalanceSelect(null)}
                  className={isTrialBalance && !groupBy ? "t-bg-purple-0" : ""}
                >
                  None
                </Dropdown.Item>
                <Dropdown.Item
                  onSelect={() => handleTrialBalanceSelect(PERIOD.MONTHLY)}
                  className={
                    isTrialBalance && isMonthGroupBy ? "t-bg-purple-0" : ""
                  }
                >
                  Months
                </Dropdown.Item>
              </Dropdown.SubContent>
            </Dropdown.Portal>
          </Dropdown.Sub>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

export { ViewAsFilter };
export type { ReportType };

export const GeneralLedgerFilters = ({ entityId }: { entityId: string }) => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  let query = parse(search, { ignoreQueryPrefix: true });
  const { appliedFiltersCount } = useSelector(getLedgerFilterStatus);
  const { isMobile } = useWindowSize();

  useEffect(() => {
    delete query.company;
    delete query.entity;
    delete query.page;
    delete query.category;
    delete query.reportType;
    delete query.groupBy;
    delete query.selected_transaction_id;
    delete query.selected_category_id;
    dispatch(resetLedgerFilter());
    dispatch(setLedgerFilter(query));
  }, [search]);

  const { values: dates, updateFilter: updateDateFilter } = useFilters({
    initialValue: {
      START_DATE: dayjs()
        .subtract(1, "month")
        .startOf("month")
        .format(YYYY_MM_DD),
      END_DATE: dayjs().subtract(1, "month").endOf("month").format(YYYY_MM_DD),
      SELECT_PERIOD: "" as string,
    },
  });

  return (
    <div className="t-flex t-justify-between t-items-center t-bg-surface t-pb-1">
      <div className="t-flex t-gap-2 t-items-center">
        <Filter.Root
          defaultValue="category"
          title={
            <span>
              {appliedFiltersCount ? <>({appliedFiltersCount})</> : ""}
            </span>
          }
          hideDivider={isMobile}
          capsule={
            <span className="t-contents sm:t-hidden">
              <CapsuleFilters entityId={entityId} />
            </span>
          }
        >
          <Filter.Portal>
            <Filter.List>
              <Filter.ListItem value="transactionDate">Date</Filter.ListItem>
              <Filter.ListItem value="category">Category</Filter.ListItem>
              <Filter.ListItem value="accounting">
                Accounting method
              </Filter.ListItem>
              <Filter.ListItem value="amount">Amount</Filter.ListItem>
              <Filter.ListItem value="others">Others</Filter.ListItem>
            </Filter.List>
            <Filter.Body value="transactionDate" block>
              <DateRangeFilter updateFilter={updateDateFilter} values={dates} />
            </Filter.Body>
            <Filter.Body value="category" block>
              <CategoryFilter />
            </Filter.Body>
            <Filter.Body value="accounting" block>
              <AccountingMethod />
            </Filter.Body>
            <Filter.Body value="amount" block>
              <Amount />
            </Filter.Body>
            <Filter.Body value="others" block>
              <OtherFilters />
            </Filter.Body>
          </Filter.Portal>
        </Filter.Root>
      </div>
    </div>
  );
};
